/**
 * R1 punt 9, 15 oktober 2026 (glasronde v0.9.155): de vaste woorden van het
 * bronblok, vertaald BIJ HET TEKENEN.
 *
 * De backend schrijft het blok in het Nederlands in de opgeslagen tekst
 * ("* bron  Knowledge: X  2 gevonden  (bron: Knowledge)"), en `agendablok.js`
 * knipt het in vier velden. Die tekst blijft zoals hij is: een opgeslagen
 * bericht wordt niet herschreven. Alleen de woorden die van ONS zijn, worden op
 * EN vertaald; documentnamen en wat de gebruiker zelf typte blijven staan.
 */

/** Velden die in hun geheel een vast woord zijn. */
export const VASTE_VELDEN = [
  "bron", "venster", "selectie", "werkruimtes", "niet geraadpleegd", "gelezen als",
  "wat opvalt", "hele dag", "geen bron", "gesprek", "systeemklok", "Geheugen",
  "de bronnen onder dit antwoord", "je eigen vraag",
  // Glasronde 0.9.166, fout 2: een gelezen bijlage als bron.
  "Bijlage",
  // GL206-4 (glasronde 0.9.206): het slotveld "(bron: Webpagina)" en "(bron: Koppeling)" bleef
  // Nederlands op een Engels scherm; alleen het voorvoegsel in de titel werd vertaald.
  "Webpagina", "Koppeling",
  // GL213-3 (glasronde 0.9.213): het label voor een kennisdocument is Nederlands ("Kennis");
  // op EN wordt het "Knowledge" via taal-en.js.
  "Kennis",
];

/**
 * GL213-3: tot 0.9.213 schreef de backend "Knowledge" in het opgeslagen blok. Een opgeslagen
 * bericht wordt niet herschreven; bij het tekenen leest dat oude woord als "Kennis". PUUR.
 */
export function oudLabel(tekst) {
  if (tekst === "Knowledge") return "Kennis";
  return tekst.startsWith("Knowledge: ") ? `Kennis: ${tekst.slice(11)}` : tekst;
}

/** Een vast woord vooraan, gevolgd door ": " en de rest. */
// Glasronde 0.9.189 vondst 9: een gelezen webpagina als bron.
// GL206-4: een koppeling als bron ("Koppeling: Direkto / search_products").
const VOORVOEGSELS = ["Geheugen", "gesprek", "systeemklok", "Bijlage", "Webpagina", "Koppeling", "Kennis"];

/** Een rest na zo'n voorvoegsel die zelf een vaste tekst is. */
export const VASTE_RESTEN = ["vorig antwoord", "de klok van deze machine"];

const GEVONDEN = /^(\d+) gevonden$/;

/**
 * R2: de vensterregel, "Fathom: gekeken op vrijdag 2026-09-11". Alles na
 * "gekeken" komt uit `services/venster.py` en is dus onze eigen tekst: een
 * gesloten woordenschat van voorzetsels, dag- en maandnamen. Alleen dan wordt
 * er woord voor woord vertaald; een veld van iemand anders raakt dit nooit.
 */
const VENSTER = /^([^:]+): gekeken (.+)$/;
export const VENSTERWOORDEN_EN = {
  "tot en met": "through", "in de laatste": "in the last", op: "on", van: "from", tot: "to",
  uur: "hours", dagen: "days",
  maandag: "Monday", dinsdag: "Tuesday", woensdag: "Wednesday", donderdag: "Thursday",
  vrijdag: "Friday", zaterdag: "Saturday", zondag: "Sunday",
  jan: "Jan", feb: "Feb", mrt: "Mar", apr: "Apr", mei: "May", jun: "Jun",
  jul: "Jul", aug: "Aug", sep: "Sep", okt: "Oct", nov: "Nov", dec: "Dec",
};
const VENSTERWOORD = new RegExp(
  `\\b(${Object.keys(VENSTERWOORDEN_EN).sort((a, b) => b.length - a.length).join("|")})\\b`, "g");

/** De vensterregel in het Engels, of null als het veld er geen is. PUUR. */
export function vensterEngels(tekst) {
  const m = VENSTER.exec(tekst);
  if (!m) return null;
  // GL207-1-a: "Google Drive: gekeken de mappen in Google Drive" draagt een selectie en geen tijd.
  const selectie = selectieEngels(m[2]);
  if (selectie) return `${m[1]}: looked at ${selectie}`;
  return `${m[1]}: looked ${m[2].replace(VENSTERWOORD, (w) => VENSTERWOORDEN_EN[w])}`;
}

/**
 * GL207-1 (glasronde 0.9.207): met de app op EN stond in de kop van het agendablok
 * "Google Calendar, zondag 2026-10-25". Het tijddeel van de kop komt uit `venster.py`
 * (`dagnaam`, `kopnaam`, `_kort`), dus uit dezelfde gesloten woordenschat als de
 * vensterregel. Alleen als het deel UITSLUITEND uit die woorden, cijfers, datums en
 * tijden bestaat, wordt het vertaald; anders blijft het staan. Zo raakt dit nooit een
 * selectie ("gesprekken over ...") of iets wat de gebruiker typte. PUUR.
 */
export function tijdEngels(deel) {
  const rest = deel.replace(VENSTERWOORD, "").replace(/\d{4}-\d\d-\d\d|\d\d:\d\d|\d+/g, "").replace(/[\s-]/g, "");
  if (rest) return null;
  return deel.replace(VENSTERWOORD, (w) => VENSTERWOORDEN_EN[w]);
}

/**
 * GL207-1-a (glasronde 0.9.207): de selectiezinnen in de kop en in een selectierij
 * ("de mappen in Google Drive", "gesprekken over ...", "taken in SPEAQ", "werkruimtes")
 * bleven Nederlands op EN. Ze komen uit de backend in deze vier vaste vormen, gemeten op
 * 141 blokken in de live database; wat erachter staat (een naam, een zoekterm) blijft staan.
 */
const SELECTIES_EN = [
  [/^de mappen in (.+)$/, "the folders in $1"],
  [/^werkruimtes$/, "workspaces"],
  [/^gesprekken over (.+)$/, "meetings about $1"],
  [/^gesprekken (".*")$/, "meetings $1"],
  [/^taken in (.+)$/, "tasks in $1"],
];

/** Een selectiezin in het Engels, of null als het er geen is. PUUR. */
export function selectieEngels(deel) {
  for (const [vorm, en] of SELECTIES_EN) {
    if (vorm.test(deel)) return deel.replace(vorm, en);
  }
  return null;
}

/**
 * GL207-2-a (glasronde 0.9.207): de storingszinnen in het bronblok ("X was niet te lezen:
 * ...") bleven Nederlands op EN. Elke vorm hier is een vaste zin uit `dagprogramma.py`
 * (`melding_bij_storing`, `reden_in_gewone_taal`, `blok_bij_storing`), `connection_store.py`
 * of `google_oauth.VERLOPEN_ZIN`. Een reden van buiten (Googles eigen tekst) blijft staan.
 * `herkoppel.js` leest de RUWE berichttekst, dus de knop Opnieuw koppelen merkt hier niets van.
 */
const REDENEN_EN = [
  [/^de opgeslagen toegang tot (.+?) kon met de sleutel van deze installatie niet worden ontsleuteld, en er is niets verstuurd\. Ontkoppel \1 en koppel opnieuw$/,
    "the stored access to $1 could not be decrypted with the key of this installation, and nothing was sent. Disconnect $1 and connect again"],
  [/^(.+?) gaf geen antwoord binnen de wachttijd$/, "$1 did not answer within the waiting time"],
  [/^de verbinding met (.+?) werd onderbroken$/, "the connection to $1 was interrupted"],
  [/^er ging iets mis en er kwam geen reden mee \(soort fout: (.+)\)$/, "something went wrong and no reason came with it (kind of error: $1)"],
  [/^Het vernieuwen van de toegang tot Google is niet gelukt: (.*)$/, "Renewing the access to Google failed: $1"],
];

/** Een reden in het Engels; een reden van buiten blijft zoals hij is. PUUR. */
function redenEngels(reden) {
  for (const [vorm, en] of REDENEN_EN) {
    if (vorm.test(reden)) return reden.replace(vorm, en);
  }
  return reden;
}

const STORINGEN_EN = [
  [/^(.+?) was niet te lezen: ([\s\S]*)$/, (m) => `${m[1]} could not be read: ${redenEngels(m[2])}`],
  [/^(.+?) was op dit moment niet te lezen: ([\s\S]*)$/, (m) => `${m[1]} could not be read at this moment: ${redenEngels(m[2])}`],
  [/^(.+?) is in korte tijd te vaak bevraagd, dus daar is nu niet gekeken\. Over een minuut kan het weer; er is niets stuk\.$/,
    (m) => `${m[1]} was asked too often in a short time, so it was not checked now. It will work again in a minute; nothing is broken.`],
  [/^(.+?) weigerde de lezing omdat de gegeven toestemming deze gegevens niet dekt\. Ontkoppel \1 en koppel opnieuw; dan vraagt NovaMund de juiste toegang\. \(([\s\S]*)\)$/,
    (m) => `${m[1]} refused the read because the permission given does not cover this data. Disconnect ${m[1]} and connect again; NovaMund will then ask for the right access. (${redenEngels(m[2])})`],
  [/^De toegang tot (.+?) is verlopen of ingetrokken bij Google\. Koppel \1 opnieuw in Instellingen > Verbindingen\.$/,
    (m) => `Access to ${m[1]} has expired or was revoked by Google. Connect ${m[1]} again in Settings > Connections.`],
  [/^(.+?): er is geen bron gekoppeld$/, (m) => `${m[1]}: no source is connected`],
  // Punt 1 (GO Frank 5 nov 2026): `NIETS_PAST` en `MEER_PASSEN` uit `services/accountkeuze.py`.
  [/^Geen gekoppeld account past bij "(.+?)"\. Gekoppeld: (.*?)\. Er is niets gelezen\. Een account toevoegen kan in Instellingen › Verbindingen › Account toevoegen\.$/,
    (m) => `No connected account matches "${m[1]}". Connected: ${m[2] === "geen" ? "none" : m[2]}. Nothing was read. You can add an account in Settings › Connections › Add account.`],
  [/^Bij "(.+?)" passen meer accounts: (.*?)\. Noem het hele adres\. Er is niets gelezen\.$/,
    (m) => `More than one account matches "${m[1]}": ${m[2]}. Name the full address. Nothing was read.`],
];

/** Een storingszin in het Engels, of null als het er geen is. PUUR. */
export function storingEngels(tekst) {
  for (const [vorm, en] of STORINGEN_EN) {
    const m = vorm.exec(tekst);
    if (m) return en(m);
  }
  return null;
}

/**
 * GL207-3-a (glasronde 0.9.209): met de app op EN stond onder "notable" nog "de dag loopt van 09:00
 * tot 12:00, 2 afspraken, 3 uur belegd". Die zinnen zijn een gesloten set uit `dagindeling.waarnemingen`
 * (en `dagprogramma._lezing` voor "gelezen als"); alleen die vormen gaan om. Wat tussen aanhalingstekens
 * staat (een afspraaknaam) en een bronnaam blijft letterlijk staan.
 */
const DUUR = /^(?:(\d+) uur)?(?: en )?(?:(\d+) minuten)?$/;

/** "4 uur en 22 minuten" in het Engels, of null als het geen duur van `_uren` is. PUUR. */
export function duurEngels(duur) {
  const m = DUUR.exec(duur || "");
  if (!m || (!m[1] && !m[2])) return null;
  const uur = m[1] && `${m[1]} ${m[1] === "1" ? "hour" : "hours"}`;
  const min = m[2] && `${m[2]} ${m[2] === "1" ? "minute" : "minutes"}`;
  return [uur, min].filter(Boolean).join(" and ");
}

const OPVALLEND_EN = [
  [/^de dag loopt van (\d\d:\d\d) tot (\d\d:\d\d), (\d+) (afspraak|afspraken), (.+) belegd$/,
    (m) => duurEngels(m[5]) && `the day runs from ${m[1]} to ${m[2]}, ${m[3]} ${m[4] === "afspraak" ? "appointment" : "appointments"}, ${duurEngels(m[5])} booked`],
  [/^(".*") staat in (.+) en is een keer geteld$/, (m) => `${m[1]} is in ${m[2].split(" en ").join(" and ")} and was counted once`],
  [/^(".*") en (".*") overlappen elkaar$/, (m) => `${m[1]} and ${m[2]} overlap`],
  [/^(".*") en (".*") sluiten op elkaar aan, er zit geen minuut tussen$/,
    (m) => `${m[1]} and ${m[2]} follow each other, with not a minute in between`],
  [/^tussen (".*") en (".*") is (.+) vrij \((\d\d:\d\d) tot (\d\d:\d\d)\)$/,
    (m) => duurEngels(m[3]) && `between ${m[1]} and ${m[2]} there is ${duurEngels(m[3])} free (${m[4]} to ${m[5]})`],
  [/^(".*") loopt de hele dag$/, (m) => `${m[1]} runs all day`],
  [/^(".*") staat er nog maar is door jou afgezegd, en telt hier niet mee$/,
    (m) => `${m[1]} is still there but you cancelled it, and it does not count here`],
  // `dagindeling`: "om 10:00" of, als het venster over een nacht loopt, "tussen 26 okt 17:03 en 27 okt 15:20".
  [/^(\d+) uit (.+) om ((?:\d+ [a-z]{3} )?\d\d:\d\d)$/, (m) => `${m[1]} from ${m[2]} at ${tijdEngels(m[3])}`],
  [/^(\d+) uit (.+) tussen ((?:\d+ [a-z]{3} )?\d\d:\d\d) en ((?:\d+ [a-z]{3} )?\d\d:\d\d)$/,
    (m) => tijdEngels(m[3]) && tijdEngels(m[4]) && `${m[1]} from ${m[2]} between ${tijdEngels(m[3])} and ${tijdEngels(m[4])}`],
  // `dagprogramma.poort`: een bronwoord met een tikfout.
  [/^(".*") is gelezen als (".*"), op een tikfout na; daarom is (.+) erbij gehaald$/,
    (m) => `${m[1]} was read as ${m[2]}, allowing for a typo; that is why ${m[3]} was included`],
];

const LEZING_EN = /^(".*") is gelezen als (\d{4}-\d\d-\d\d); het woord (".*") wees naar (\d{4}-\d\d-\d\d) en is genegeerd, want een datum noemt zijn eigen dag en een weekdag niet$/;

/** Een zin uit "wat opvalt" of "gelezen als" in het Engels, of null als het er geen is. PUUR. */
export function opvallendEngels(tekst) {
  const l = LEZING_EN.exec(tekst || "");
  if (l) return `${l[1]} was read as ${l[2]}; the word ${l[3]} pointed to ${l[4]} and was ignored, because a date names its own day and a weekday does not`;
  for (const [vorm, en] of OPVALLEND_EN) {
    const m = vorm.exec(tekst || "");
    if (m) return en(m) || null;
  }
  return null;
}

/** De kop van het blok: bronnen, dan het tijddeel, dan eventueel "(account)". */
const KOP = /^([^,]+), (.+?)( \([^)]*\))?$/;

/** De kop van het agendablok in de taal van `t`. PUUR, op `t` na. */
export function kopVeld(t, kop) {
  const tekst = (kop || "").trim();
  if (!tekst || t("bron") === "bron") return vertaalVeld(t, kop);
  const m = KOP.exec(tekst);
  const tijd = m && (tijdEngels(m[2]) ?? selectieEngels(m[2]));
  if (!tijd) return vertaalVeld(t, kop);
  const naam = VASTE_VELDEN.includes(m[1]) ? t(m[1]) : m[1];
  return `${naam}, ${tijd}${m[3] || ""}`;
}

/**
 * GL212-3 (glasronde 0.9.212, Frank 28 oktober 2026): de kop van het bronnenblok als een nette zin.
 *
 * Bij een kennisvraag zet de backend als herkomst "de bronnen onder dit antwoord" (`bronblok.py`,
 * `KOP_ZONDER_DAG`). Het scherm zette daar "uit" voor, en dan stond er "uit  de bronnen onder dit
 * antwoord". Die herkomst is al een hele kop; hij krijgt de nette vorm en geen "uit". Een kop met
 * een bron en een dag ("Google Agenda, 28 okt") houdt zijn "uit", net als een blok zonder kop. PUUR.
 */
export const KOP_ZONDER_DAG = "de bronnen onder dit antwoord";

export function blokKop(t, herkomst) {
  const tekst = (herkomst || "").trim();
  if (tekst === KOP_ZONDER_DAG) return { voor: "", kop: t("Bronnen onder dit antwoord") };
  // GL214-1 (glasronde 0.9.214): een kop die een SELECTIE is ("Fathom, gesprekken over ...") krijgt
  // dezelfde nette kop; wat er is gezocht, staat al in de selectierij eronder. Een dagkop houdt "uit".
  const m = KOP.exec(tekst);
  // Alleen Fathom: de selectiekop van TASQ en Drive blijft (GL207-1-a vertaalt hem).
  if (m && m[1].trim() === "Fathom" && selectieEngels(m[2])) return { voor: "", kop: t("Bronnen onder dit antwoord") };
  return { voor: t("uit"), kop: tekst ? kopVeld(t, herkomst) : t("je bronnen") };
}

/** GL212-3: "bron" in de tijdkolom is een plaatshouder en geen tijd; die staat niet in beeld. PUUR. */
export function toonWanneer(veld) {
  // GL214-1: ook "selectie" is een plaatshouder en geen tijd; het stond in codeletter in beeld.
  return Boolean((veld || "").trim()) && !["bron", "selectie"].includes((veld || "").trim());
}

/**
 * GL214-2 (glasronde 0.9.214, Frank 28 oktober 2026): een gesprek uit Fathom stond als ruwe tekst in
 * het blok: "[met: ...]", "[samenvatting: [**Platform Validated ...", "[terugkijken: ...]". Leesbaar
 * zijn titel, datum, deelnemers en EEN klikbare link. De samenvatting blijft in het materiaal voor het
 * model en komt hier niet in beeld. Geeft null voor een rij die niet uit Fathom komt. PUUR.
 */
export function fathomRij(rij) {
  // Alleen een gesprek: een rij met een tijd. De selectie- en vensterregel van Fathom blijven gewone rijen.
  if (!/^Fathom\b/.test((rij?.bron || "").trim()) || !/\d\d:\d\d/.test(rij.wanneer || "")) return null;
  const extras = rij.extras || "";
  const veld = (label) => {
    const m = new RegExp(`\\[${label}: ([^\\]]*)\\]`).exec(extras);
    return m ? m[1].trim() : "";
  };
  const link = /\[terugkijken: (https:\/\/[^\]\s]+)\]/.exec(extras);
  return {
    titel: rij.titel || "",
    datum: veld("op"),
    tijd: (rij.wanneer || "").trim(),
    deelnemers: veld("met"),
    link: link ? link[1] : "",
  };
}

/** Een datum als "8 oktober 2026" in de taal van `t`. PUUR, op `t` na. */
export function datumVeld(t, datum) {
  if (t("bron") === "bron") return datum;
  return (datum || "").replace(/\b([a-z]+)\b/g, (w) => MAANDEN_EN[w] || w);
}

const NAAM_EN_REST = /^([^:]+): (.+)$/;

/**
 * De titel van een rij in de taal van `t`. PUUR, op `t` na.
 *
 * **DE SOORT VAN DE RIJ BESLIST, NIET DE TEKST ALLEEN.** Een titel is ook een mailonderwerp of
 * een afspraaknaam, en "Re: taken in Q4" is geen selectie. Een selectiezin wordt dus alleen
 * vertaald op een `selectie`-rij en een storingszin alleen op een `niet geraadpleegd`-rij.
 */
export function titelVeld(t, rij) {
  const titel = rij?.titel;
  if (t("bron") === "bron") return vertaalVeld(t, titel);
  const tekst = (titel || "").trim();
  if (rij?.wanneer === "niet geraadpleegd") return storingEngels(tekst) ?? vertaalVeld(t, titel);
  // GL207-3-a: alleen op hun eigen rij, want een afspraak kan ook "... overlappen elkaar" heten.
  if (rij?.wanneer === "wat opvalt" || rij?.wanneer === "gelezen als") {
    return opvallendEngels(tekst) ?? vertaalVeld(t, titel);
  }
  if (rij?.wanneer === "selectie") {
    const m = NAAM_EN_REST.exec(tekst);
    const en = m && selectieEngels(m[2]);
    if (en) return `${m[1]}: ${en}`;
  }
  return vertaalVeld(t, titel);
}

/**
 * GL208-1 (glasronde 0.9.208): met de app op EN stonden in het agendablok nog "[plaats: ...]" en
 * "[met: ...]" in het Nederlands. De labels komen uit een gesloten set in `dagindeling.py` en
 * `dagprogramma.py`; ze worden alle vertaald, want het is dezelfde soort tekst. Alleen het LABEL
 * en de paar vaste waarden van ons gaan om; wat erachter staat (een plaats, een naam, een
 * onderwerp) blijft letterlijk staan.
 */
export const LABELS_EN = {
  plaats: "location", met: "with", van: "from", link: "link", adres: "address", begin: "start",
  soort: "type", status: "status", prioriteit: "priority", "in TASQ": "in TASQ",
  werkruimte: "workspace", deadline: "deadline", "opgenomen door": "recorded by",
  samenvatting: "summary", terugkijken: "watch", "open taken": "open tasks",
};
const LABEL = new RegExp(
  `(^|\\] )\\[(${Object.keys(LABELS_EN).sort((a, b) => b.length - a.length).join("|")}): `, "g");
/** De vaste waarden van `google_drive.SOORTEN`; een onbekend mimetype blijft staan. */
const SOORTEN_EN = { map: "folder", presentatie: "presentation", formulier: "form", bestand: "file" };
const MAANDEN_EN = {
  januari: "January", februari: "February", maart: "March", april: "April", mei: "May",
  juni: "June", juli: "July", augustus: "August", september: "September", oktober: "October",
  november: "November", december: "December",
};

/**
 * De deelnemers van een gesprek in de taal van `t`: de telling achteraan ("en 1 andere") wordt
 * "and 1 other". PUUR, op `t` na. GL217-2 (glasronde 0.9.216): de Fathom-rij toonde de deelnemers
 * rauw en kreeg deze vertaling niet; nu gebruiken de Fathom-rij en `extrasVeld` deze ene functie.
 */
export function deelnemersVeld(t, namen) {
  if (t("bron") === "bron" || !namen) return namen;
  return namen
    .replace(/ en 1 andere$/, " and 1 other")
    .replace(/ en (\d+) anderen$/, " and $1 others");
}

/** Het extraveld van een rij in de taal van `t`. PUUR, op `t` na. */
export function extrasVeld(t, extras) {
  const vast = vertaalVeld(t, extras);
  if (vast !== extras || t("bron") === "bron" || !extras) return vast;
  return extras
    .replace(/\[soort: ([^\]]+)\]/g, (w, v) => `[soort: ${SOORTEN_EN[v] || v}]`)
    .replace(/\[deadline: (\d+) ([a-z]+) /g, (w, d, m) => `[deadline: ${d} ${MAANDEN_EN[m] || m} `)
    .replace(/\[met: ([^\]]*)\]/g, (w, namen) => `[met: ${deelnemersVeld(t, namen)}]`)
    .replace(/\[afgezegd door jou\]/g, "[cancelled by you]")
    .replace(LABEL, (w, voor, label) => `${voor}[${LABELS_EN[label]}: `);
}

/**
 * GL208-2 (glasronde 0.9.208): onder elke afspraak stond een lange agenda-id die rechts buiten
 * beeld liep. Bij een AFSPRAAK (Google Calendar, Fathom, of beide) is de id geen informatie voor
 * de lezer; hij blijft wel vindbaar als tooltip, want hij is de weg terug naar de bron (R57).
 * Bij mail blijft de bron ongemoeid: `mailUit` leest daar het bericht-id voor de antwoordknop,
 * en dat leest het RUWE veld, niet wat hier getoond wordt. PUUR.
 */
const AFSPRAAKBRON = /^((?:Google Calendar|Fathom)(?:\/(?:Google Calendar|Fathom))*),\s*id\s+(.+)$/;

/** De bron van een rij zoals hij getoond wordt, met de id's apart. PUUR. */
export function bronZonderId(bron) {
  const m = AFSPRAAKBRON.exec((bron || "").trim());
  return m ? { naam: m[1], ids: m[2].trim() } : { naam: bron, ids: null };
}

/** Het tijdveld van een rij ("25 okt 10:00") in de taal van `t`. PUUR, op `t` na. */
export function wanneerVeld(t, veld) {
  const vast = vertaalVeld(t, veld);
  if (vast !== veld || t("bron") === "bron") return vast;
  return tijdEngels((veld || "").trim()) ?? veld;
}

/** Een veld uit het bronblok in de taal van `t`. PUUR, op `t` na. */
export function vertaalVeld(t, veld) {
  const tekst = oudLabel((veld || "").trim());
  if (!tekst) return veld;
  if (VASTE_VELDEN.includes(tekst)) return t(tekst);
  const g = GEVONDEN.exec(tekst);
  if (g) return t("{n} gevonden", { n: g[1] });
  // De vensterregel heeft geen sleutel per zin; `t` zegt alleen welke taal het is.
  if (t("bron") !== "bron") {
    const venster = vensterEngels(tekst);
    if (venster) return venster;
  }
  for (const kop of VOORVOEGSELS) {
    if (tekst.startsWith(`${kop}: `)) {
      const rest = tekst.slice(kop.length + 2);
      return `${t(kop)}: ${VASTE_RESTEN.includes(rest) ? t(rest) : rest}`;
    }
  }
  return tekst === (veld || "").trim() ? veld : tekst;
}

/**
 * C3, 15 oktober 2026: "vijf zinnen gaf zes". Onder het antwoord stond de
 * bronregel "Geheugen: Plexaris is een technologiebedrijf dat digitale...", en
 * die las als een zesde zin. Een geheugenregel is een CITAAT uit wat jij liet
 * onthouden, dus hij komt tussen aanhalingstekens. PUUR.
 */
export function alsCitaat(titel) {
  const m = /^(Geheugen|Memory): (.+)$/.exec(titel || "");
  if (!m || /^["“]/.test(m[2])) return titel;
  return `${m[1]}: \u201c${m[2]}\u201d`;
}
